import type { EndpointWithResources } from '@/types/endpoint';
import type { App } from '@/types/app';
import type { Catalog } from '@/types/catalog';
import { RUNNING_STATUSES } from './appStatus.ts';

export interface EndpointHealth { up: number; down: number; unknown: number; }
export interface AppHealth { running: number; stopped: number; failed: number; }
export interface ResourceUsage { available: number; allocated: number; percent: number; }
export interface Capacity { cpu: ResourceUsage; memory: ResourceUsage; storage: ResourceUsage; }

export function computeEndpointHealth(endpoints: EndpointWithResources[]): EndpointHealth {
  if (!Array.isArray(endpoints)) return { up: 0, down: 0, unknown: 0 };
  return {
    up: endpoints.filter(e => e.status === 'up').length,
    down: endpoints.filter(e => e.status === 'down').length,
    unknown: endpoints.filter(e => e.status === 'unknown').length,
  };
}

export function computeAppHealth(apps: App[]): AppHealth {
  if (!Array.isArray(apps)) return { running: 0, stopped: 0, failed: 0 };
  return {
    running: apps.filter(a => RUNNING_STATUSES.includes(a.status)).length,
    stopped: apps.filter(a => a.status === 'stopped').length,
    failed: apps.filter(a => a.status === 'failed').length,
  };
}

function usage(available: number, allocated: number): ResourceUsage {
  const percent = available > 0 ? Math.round((allocated / available) * 100) : 0;
  return { available, allocated, percent };
}

/**
 * Aggregate capacity across all endpoints, mirroring the backend's
 * capacity.Compute: `allocated` (used) = sum of deployed app stack limits
 * bounded by host totals; `available` = sum of host totals.
 *
 * Precedence per endpoint (same as `endpointRemainingCapacity`):
 *   1. `endpoint.capacity` — API-computed used/total block (authoritative).
 *   2. Host totals (cpu_total etc.) — used computed from this endpoint's apps.
 *   3. Heuristic fallback: `resources.*_available` as the total.
 *
 * Using `resources.*_available` (a *remaining* number discounted by deployed
 * limits) as the denominator — the old behaviour — produced nonsense like
 * (5.25 cores allocated / 3.1 cores available) = 169%, because the numerator
 * (sum of app limits) double-counted capacity the denominator had already
 * subtracted. With totals as the denominator the two cards agree and the
 * percent is a real (used / total) ratio.
 */
export function computeCapacity(endpoints: EndpointWithResources[], apps: App[]): Capacity {
  const eps = Array.isArray(endpoints) ? endpoints : [];
  const as = Array.isArray(apps) ? apps : [];

  let cpuTotal = 0, memTotal = 0, stoTotal = 0;
  let cpuUsed = 0, memUsed = 0, stoUsed = 0;

  for (const ep of eps) {
    if (ep.capacity) {
      cpuTotal += ep.capacity.cpu_total;
      memTotal += ep.capacity.memory_total_mb;
      stoTotal += ep.capacity.storage_total_gb;
      cpuUsed += ep.capacity.cpu_used;
      memUsed += ep.capacity.memory_used_mb;
      stoUsed += ep.capacity.storage_used_gb;
    } else if (
      ep.cpu_total != null && ep.memory_total_mb != null && ep.storage_total_gb != null
    ) {
      const alloc = allocatedOnEndpoint(ep.id, as);
      cpuTotal += ep.cpu_total;
      memTotal += ep.memory_total_mb;
      stoTotal += ep.storage_total_gb;
      cpuUsed += alloc.cpu;
      memUsed += alloc.memory;
      stoUsed += alloc.storage;
    } else {
      // Heuristic fallback: legacy single-number *_available is already a
      // remaining figure, so used must be derived from this endpoint's apps.
      const alloc = allocatedOnEndpoint(ep.id, as);
      cpuTotal += ep.resources?.cpu_available ?? 0;
      memTotal += ep.resources?.memory_available_mb ?? 0;
      stoTotal += ep.resources?.storage_available_gb ?? 0;
      cpuUsed += alloc.cpu;
      memUsed += alloc.memory;
      stoUsed += alloc.storage;
    }
  }

  return {
    cpu: usage(cpuTotal, cpuUsed),
    memory: usage(memTotal, memUsed),
    storage: usage(stoTotal, stoUsed),
  };
}

export function recentApps(apps: App[], limit: number): App[] {
  if (!Array.isArray(apps)) return [];
  return [...apps]
    .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))
    .slice(0, limit);
}

export function endpointsNeedingAttention(endpoints: EndpointWithResources[]): EndpointWithResources[] {
  if (!Array.isArray(endpoints)) return [];
  return endpoints.filter(e => e.status === 'down' || e.status === 'unknown');
}

export function recentCatalogs(catalogs: Catalog[], limit: number): Catalog[] {
  if (!Array.isArray(catalogs)) return [];
  return [...catalogs]
    .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))
    .slice(0, limit);
}

export function deployedCountByCatalog(apps: App[]): Record<number, number> {
  if (!Array.isArray(apps)) return {};
  const counts: Record<number, number> = {};
  for (const a of apps) {
    const id = a.app_catalog?.id;
    if (id == null) continue;
    counts[id] = (counts[id] ?? 0) + 1;
  }
  return counts;
}

export interface EndpointCapacityItem {
  id: number;
  name: string;
  hasResources: boolean;
  cpu: ResourceUsage;
  memory: ResourceUsage;
  storage: ResourceUsage;
}

export interface RemainingCapacity {
  /** Remaining CPU cores (total - used). */
  cpu: number;
  /** Remaining memory in MB. */
  memoryMb: number;
  /** Remaining storage in GB. */
  storageGb: number;
  /**
   * true when host totals (cpu_total / memory_total_mb / storage_total_gb)
   * were available and the residual is computed via the sum-of-limits
   * semantics. false means we fell back to the heuristic
   * `resources.*_available` numbers.
   */
  hasTotals: boolean;
}

/**
 * Sum of deployed app stack limits on the given endpoint. Matches the
 * backend's capacity.Compute: ALL apps on the endpoint count (no status
 * filter) because their limits are reserved while the app exists.
 */
function allocatedOnEndpoint(endpointId: number, apps: App[]) {
  const filtered = (apps ?? []).filter(a => a.endpoint?.id === endpointId);
  return {
    cpu: filtered.reduce((s, a) => s + (a.stack?.cpu_limit ?? 0), 0),
    memory: filtered.reduce((s, a) => s + (a.stack?.memory_mb ?? 0), 0),
    storage: filtered.reduce((s, a) => s + (a.stack?.storage_gb ?? 0), 0),
  };
}

/**
 * Per-endpoint remaining (free) capacity, shared by the dashboard and the
 * deploy form so they always agree with the backend's sum-of-limits
 * validation.
 *
 * Precedence:
 *   1. `endpoint.capacity` (API-computed used/total, now emitted by both
 *      GET /endpoints and GET /endpoints/:id/resources) when present —
 *      authoritative, already computed by the backend. The `apps` argument
 *      is IGNORED on this path, so callers without a local apps list
 *      (e.g. NewAppPage, which now reads the List capacity block directly)
 *      get correct residuals.
 *   2. Host totals (cpu_total etc.) on the endpoint: `used` is summed from
 *      the supplied `apps` (mirrors capacity.Compute), `total` from the
 *      totals. Requires the apps argument.
 *   3. Heuristic fallback: `endpoint.resources.*_available` (the old
 *      single-number heuristic). `hasTotals` is false so callers can opt to
 *      keep the legacy threshold behaviour and avoid regressing when totals
 *      haven't been synced.
 */
export function endpointRemainingCapacity(
  endpoint: EndpointWithResources,
  apps?: App[]
): RemainingCapacity {
  // Path 1: API-computed capacity block.
  if (endpoint.capacity) {
    return {
      cpu: endpoint.capacity.cpu_total - endpoint.capacity.cpu_used,
      memoryMb: endpoint.capacity.memory_total_mb - endpoint.capacity.memory_used_mb,
      storageGb: endpoint.capacity.storage_total_gb - endpoint.capacity.storage_used_gb,
      hasTotals: true,
    };
  }

  // Path 2: host totals present — compute used from apps.
  if (
    endpoint.cpu_total != null &&
    endpoint.memory_total_mb != null &&
    endpoint.storage_total_gb != null
  ) {
    const used = allocatedOnEndpoint(endpoint.id, apps ?? []);
    return {
      cpu: endpoint.cpu_total - used.cpu,
      memoryMb: endpoint.memory_total_mb - used.memory,
      storageGb: endpoint.storage_total_gb - used.storage,
      hasTotals: true,
    };
  }

  // Path 3: heuristic fallback (resources.*_available).
  return {
    cpu: endpoint.resources?.cpu_available ?? 0,
    memoryMb: endpoint.resources?.memory_available_mb ?? 0,
    storageGb: endpoint.resources?.storage_available_gb ?? 0,
    hasTotals: false,
  };
}

export function endpointCapacity(
  endpoints: EndpointWithResources[],
  apps: App[]
): EndpointCapacityItem[] {
  const eps = Array.isArray(endpoints) ? endpoints : [];
  const as = Array.isArray(apps) ? apps : [];

  return eps.map(ep => {
    const rem = endpointRemainingCapacity(ep, as);
    const hasResources = !!ep.resources || rem.hasTotals;

    // Under the new sum-of-limits model `used` = total - remaining, and the
    // ResourceUsage.available field holds the endpoint's TOTAL capacity
    // (so percent = used/total). When totals are unknown we keep the legacy
    // single-number heuristic semantics for backward compatibility:
    //   available = heuristic *_available, allocated = used-from-apps.
    let usedCpu: number, usedMem: number, usedSto: number;
    let totalCpu: number, totalMem: number, totalSto: number;

    if (rem.hasTotals) {
      totalCpu = ep.capacity?.cpu_total ?? ep.cpu_total ?? 0;
      totalMem = ep.capacity?.memory_total_mb ?? ep.memory_total_mb ?? 0;
      totalSto = ep.capacity?.storage_total_gb ?? ep.storage_total_gb ?? 0;
      usedCpu = totalCpu - rem.cpu;
      usedMem = totalMem - rem.memoryMb;
      usedSto = totalSto - rem.storageGb;
    } else {
      // Heuristic fallback: allocated computed from apps, "available" is the
      // legacy single-number heuristic (which already discounts used).
      const alloc = allocatedOnEndpoint(ep.id, as);
      usedCpu = alloc.cpu;
      usedMem = alloc.memory;
      usedSto = alloc.storage;
      totalCpu = ep.resources?.cpu_available ?? 0;
      totalMem = ep.resources?.memory_available_mb ?? 0;
      totalSto = ep.resources?.storage_available_gb ?? 0;
    }

    return {
      id: ep.id,
      name: ep.name,
      hasResources,
      cpu: usage(totalCpu, usedCpu),
      memory: usage(totalMem, usedMem),
      storage: usage(totalSto, usedSto),
    };
  });
}
